import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getInvoiceForBooking, BookingInvoice } from '../lib/invoices/getInvoiceForBooking';

/**
 * Live invoice data for a booking. Reads the new `invoices` table and subscribes
 * to realtime changes so the payment summary reflects booking edits instantly.
 *
 * Returns `invoice: null` for bookings that have no invoice yet (legacy bookings),
 * letting callers fall back to legacy booking totals.
 */
export function useBookingInvoice(bookingId?: string | null) {
  const [invoice, setInvoice] = useState<BookingInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(!!bookingId);
  const activeRef = useRef(true);

  const refetch = useCallback(async () => {
    if (!bookingId) {
      setInvoice(null);
      setLoading(false);
      return;
    }
    const inv = await getInvoiceForBooking(bookingId);
    if (activeRef.current) {
      setInvoice(inv);
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    activeRef.current = true;

    if (!bookingId) {
      setInvoice(null);
      setLoading(false);
      return () => {
        activeRef.current = false;
      };
    }

    setLoading(true);
    refetch();

    // Header rolls up line-item changes via DB triggers, so subscribing to the
    // invoice row (filtered by booking) captures all live total/tax updates.
    // Unique channel suffix so multiple components can watch the same booking.
    const channel = supabase
      .channel(`booking-invoice-${bookingId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'invoices', filter: `booking_id=eq.${bookingId}` },
        () => {
          refetch();
        },
      )
      .subscribe();

    return () => {
      activeRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [bookingId, refetch]);

  return { invoice, loading, refetch };
}
